/**
 * Web Vitals 性能监控工具
 *
 * 提供 Core Web Vitals 性能指标的采集和报告功能。
 * 默认仅在开发模式下输出到控制台，也支持自定义上报。
 *
 */

/**
 * 性能指标数据
 */
export interface PerformanceMetric {
  /** 指标名称 */
  name: string;
  /** 指标值 */
  value: number;
  /** 评级 (good / needs-improvement / poor) */
  rating?: string;
  /** 导航类型 */
  navigationType?: string;
}

/**
 * 性能监控配置
 */
export interface PerformanceMonitoringOptions {
  /** 是否启用，默认 false（消费方根据自身环境判断后显式开启） */
  enabled?: boolean;
  /** 指标上报回调 */
  onMetric?: (metric: PerformanceMetric) => void;
  /** 是否在控制台输出，默认 true */
  consoleOutput?: boolean;
}

/**
 * 初始化性能监控
 *
 * 采集 Core Web Vitals 指标：
 * - LCP (Largest Contentful Paint): 最大内容绘制时间
 * - INP (Interaction to Next Paint): 交互到下一次绘制延迟（2024 年 3 月起取代 FID）
 * - CLS (Cumulative Layout Shift): 累积布局偏移
 * - FCP (First Contentful Paint): 首次内容绘制
 * - TTFB (Time to First Byte): 首字节到达时间
 *
 * @param options - 配置选项
 *
 * @example
 * ```ts
 * // 开发模式：显式启用并输出到控制台
 * initPerformanceMonitoring({ enabled: import.meta.env.DEV });
 *
 * // 生产模式：上报到分析服务
 * initPerformanceMonitoring({
 *   enabled: true,
 *   consoleOutput: false,
 *   onMetric: (metric) => {
 *     analytics.track('web_vital', metric);
 *   },
 * });
 * ```
 */
/**
 * 清理函数类型，调用后断开所有观察器
 */
export type PerformanceCleanup = () => void;

export function initPerformanceMonitoring(
  options?: PerformanceMonitoringOptions,
): PerformanceCleanup {
  const { enabled = false, onMetric, consoleOutput = true } = options ?? {};

  const noop = () => {};

  if (!enabled) return noop;
  if (
    typeof window === 'undefined' ||
    typeof PerformanceObserver === 'undefined'
  )
    return noop;

  // 收集所有 observer 以便清理
  const observers: PerformanceObserver[] = [];

  const reportMetric = (metric: PerformanceMetric) => {
    if (consoleOutput) {
      const color = getMetricColor(metric.name, metric.value);
      console.info(
        `%c[WebVitals] ${metric.name}: ${metric.value.toFixed(2)}${metric.rating ? ` (${metric.rating})` : ''}`,
        `color: ${color}; font-weight: bold;`,
      );
    }
    onMetric?.(metric);
  };

  // LCP - Largest Contentful Paint
  try {
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        reportMetric({
          name: 'LCP',
          value: lastEntry.startTime,
          rating:
            lastEntry.startTime <= 2500
              ? 'good'
              : lastEntry.startTime <= 4000
                ? 'needs-improvement'
                : 'poor',
        });
      }
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    observers.push(lcpObserver);
  } catch {
    // LCP observer not supported
  }

  // INP - Interaction to Next Paint (取代 FID，2024-03 起成为 Core Web Vital)
  try {
    let maxInp = 0;
    const inpObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        const evt = entry as PerformanceEventTiming & {
          interactionId?: number;
        };
        // 仅统计有 interactionId 的事件（排除非用户交互事件）
        if (evt.interactionId && evt.interactionId > 0) {
          const duration = evt.duration;
          if (duration > maxInp) {
            maxInp = duration;
            reportMetric({
              name: 'INP',
              value: duration,
              rating:
                duration <= 200
                  ? 'good'
                  : duration <= 500
                    ? 'needs-improvement'
                    : 'poor',
            });
          }
        }
      }
    });
    inpObserver.observe({
      type: 'event',
      buffered: true,
      durationThreshold: 16,
    } as PerformanceObserverInit);
    observers.push(inpObserver);
  } catch {
    // INP observer not supported
  }

  // CLS - Cumulative Layout Shift
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        if (
          !(entry as PerformanceEntry & { hadRecentInput?: boolean })
            .hadRecentInput
        ) {
          clsValue +=
            (entry as PerformanceEntry & { value?: number }).value ?? 0;
        }
      }
      reportMetric({
        name: 'CLS',
        value: clsValue,
        rating:
          clsValue <= 0.1
            ? 'good'
            : clsValue <= 0.25
              ? 'needs-improvement'
              : 'poor',
      });
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });
    observers.push(clsObserver);
  } catch {
    // CLS observer not supported
  }

  // FCP - First Contentful Paint
  try {
    const fcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const fcpEntry = entries.find((e) => e.name === 'first-contentful-paint');
      if (fcpEntry) {
        reportMetric({
          name: 'FCP',
          value: fcpEntry.startTime,
          rating:
            fcpEntry.startTime <= 1800
              ? 'good'
              : fcpEntry.startTime <= 3000
                ? 'needs-improvement'
                : 'poor',
        });
      }
    });
    fcpObserver.observe({ type: 'paint', buffered: true });
    observers.push(fcpObserver);
  } catch {
    // FCP observer not supported
  }

  // TTFB - Time to First Byte
  try {
    const navEntries = performance.getEntriesByType('navigation');
    if (navEntries.length > 0) {
      const nav = navEntries[0] as PerformanceNavigationTiming;
      const ttfb = nav.responseStart - nav.requestStart;
      if (ttfb >= 0) {
        reportMetric({
          name: 'TTFB',
          value: ttfb,
          rating:
            ttfb <= 800 ? 'good' : ttfb <= 1800 ? 'needs-improvement' : 'poor',
        });
      }
    }
  } catch {
    // Navigation timing not supported
  }

  // 返回清理函数，断开所有 observer 防止内存泄漏
  return () => {
    for (const observer of observers) {
      observer.disconnect();
    }
    observers.length = 0;
  };
}

/**
 * 获取指标对应的颜色（控制台输出用）
 */
function getMetricColor(name: string, value: number): string {
  const thresholds: Record<string, [number, number]> = {
    LCP: [2500, 4000],
    INP: [200, 500],
    CLS: [0.1, 0.25],
    FCP: [1800, 3000],
    TTFB: [800, 1800],
  };

  const [good, poor] = thresholds[name] ?? [Infinity, Infinity];
  if (value <= good) return '#0cce6b'; // green
  if (value <= poor) return '#ffa400'; // orange
  return '#ff4e42'; // red
}
